'use client';

import { Button } from '@ahafood/ui/components/button';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import InlineLoadingSkeletons from '../inline-loading-skeletons';
import SoloMealItemCard from '../solo-meal-item-card';
import { getTrendingItemFn } from '@/api/use-get-trending-item';
import { LoginDialog } from '@/app/[locale]/(main)/store/components/fb-login';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { Link } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useAuthStore } from '@/store';

const LIMIT = 20;

export default function TrendingItemList({
  lat,
  lng,
}: {
  lat: number;
  lng: number;
}) {
  const routes = useCityRoutes();
  const { isAuthenticated } = useAuthStore();
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  useEffect(() => {
    if (isAuthenticated) setShowLoginDialog(false);
  }, [isAuthenticated]);

  const trackItemClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'trending_card_click',
      button_location: 'trending_zone',
    });
  };

  const trackAddToCart = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'trending_add_to_cart',
      button_location: 'trending_zone',
    });
  };

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['trending-items', { lat, lng }],
    queryFn: ({ pageParam = 0 }) =>
      getTrendingItemFn({ lat, lng, limit: LIMIT, skip: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (acc, page) => acc + (page?.trending_items?.length || 0),
        0
      );
      return lastPage.trending_items.length < LIMIT ? undefined : loaded;
    },
    initialPageParam: 0,
    // staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    // gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const trendingItems = useMemo(
    () =>
      data?.pages?.flatMap((page) => page.trending_items).filter(Boolean) ?? [],
    [data]
  );

  const renderHeader = (
    <div className="mb-4 pt-4">
      <h2 className="text-lg font-bold text-neutral-90 mb-1">
        Danh sách món xu hướng gần bạn
      </h2>
    </div>
  );

  if (isLoading && !trendingItems.length) {
    return (
      <div className="px-4 pb-20 w-full max-w-screen-xl mx-auto">
        {renderHeader}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
          <InlineLoadingSkeletons />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-20 w-full max-w-screen-xl mx-auto">
      {renderHeader}
      <InfiniteScroll
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4"
        dataLength={trendingItems.length}
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        loader={<InlineLoadingSkeletons />}
        endMessage={
          <div className="col-span-full text-center py-8">
            <Link href={routes.home}>
              <Button
                variant="outline"
                className="border-primary w-full font-bold text-primary"
              >
                Khám phá thêm Quán ăn
              </Button>
            </Link>
          </div>
        }
      >
        {trendingItems.length ? (
          trendingItems.map((item, index) => (
            <SoloMealItemCard
              onClick={trackItemClick}
              key={item._id}
              item={item}
              index={index}
              isAuthenticated={isAuthenticated}
              setShowLoginDialog={setShowLoginDialog}
              onAddToCartClick={trackAddToCart}
            />
          ))
        ) : (
          <div className="col-span-full text-center py-8 text-neutral-60">
            Không có món ăn nào.
          </div>
        )}
      </InfiniteScroll>

      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="trending_item_list"
      />
    </div>
  );
}
